import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import aiService from '../services/aiService';
import authService from '../services/authService';
import businessService from '../services/businessService';
import BackToDashboard from '../components/BackToDashboard';

function Vision() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('input'); // input, generated, editing, complete
  const [formData, setFormData] = useState({
    customer_entitlement: '',
    how_you_help: '',
    superpower: ''
  });
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [editedVision, setEditedVision] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedVision, setSavedVision] = useState('');
  const [visionHint, setVisionHint] = useState('');

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load existing vision statement if any
    const result = await businessService.getField('vision_statement');
    if (result.success && result.data.field_value) {
      setSavedVision(result.data.field_value);
      setStage('complete');
    }

    // Load previous form inputs and scraped hints
    const fieldsResult = await businessService.getFieldsByNames([
      'customer_entitlement', 'how_you_help', 'superpower',
      'vision_hint', 'transformation_promise', 'value_proposition', 'competitive_advantages'
    ]);
    if (fieldsResult.success && fieldsResult.data.fields) {
      const fields = fieldsResult.data.fields;

      // Pre-fill form with saved or scraped data
      setFormData({
        customer_entitlement: fields.customer_entitlement || fields.transformation_promise || '',
        how_you_help: fields.how_you_help || fields.value_proposition || '',
        superpower: fields.superpower || fields.competitive_advantages || ''
      });

      // Load vision hint from scraper
      if (fields.vision_hint) {
        setVisionHint(fields.vision_hint);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const generateOptions = async () => {
    setLoading(true);
    setError('');

    const result = await aiService.generateVision(formData);

    setLoading(false);

    if (result.success) {
      setOptions(result.data.options);
      setStage('generated');
    } else {
      setError(result.error);
    }
  };

  const selectOption = (index) => {
    setSelectedOption(index);
    setEditedVision(options[index]);
  };

  const handleEditChange = (e) => {
    setEditedVision(e.target.value);
  };

  const saveVision = async () => {
    if (!editedVision.trim()) {
      setError('Please select or enter a vision statement');
      return;
    }

    setLoading(true);
    setError('');

    const result = await aiService.saveVision({
      vision_statement: editedVision,
      ...formData
    });

    setLoading(false);

    if (result.success) {
      setSavedVision(editedVision);
      setStage('complete');
    } else {
      setError(result.error);
    }
  };

  const regenerate = async () => {
    setSelectedOption(null);
    setEditedVision('');
    await generateOptions();
  };

  const renderInput = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Vision Statement</h2>

        <p className="text-muted mb-4">
          Your vision statement paints a picture of the future you're working to create.
          It answers "why" your business exists and the bigger purpose behind your work.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        {visionHint && (
          <Alert variant="info" className="mb-4">
            <strong>From your website:</strong> "{visionHint}"
            <br />
            <small className="text-muted">We've pre-filled the form below based on what we found. Edit as needed.</small>
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3" controlId="customer_entitlement">
            <Form.Label><strong>What do you believe your customers are entitled to?</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="customer_entitlement"
              value={formData.customer_entitlement}
              onChange={handleChange}
              placeholder="e.g., honest advice, quality service, results that matter"
              required
            />
            <Form.Text className="text-muted">
              What do your customers deserve from you?
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="how_you_help">
            <Form.Label><strong>How do you help them achieve this?</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="how_you_help"
              value={formData.how_you_help}
              onChange={handleChange}
              placeholder="e.g., personalized coaching, cutting-edge tools, step-by-step guidance"
              required
            />
            <Form.Text className="text-muted">
              What's your approach or methodology?
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-4" controlId="superpower">
            <Form.Label><strong>What SUPERPOWER do you bring to the equation?</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="superpower"
              value={formData.superpower}
              onChange={handleChange}
              placeholder="e.g., 20 years of experience, unique insights, proven methodology"
              required
            />
            <Form.Text className="text-muted">
              What makes you uniquely qualified or different?
            </Form.Text>
          </Form.Group>

          <div className="d-flex justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/dashboard/foundation')}
            >
              Save for Later
            </Button>
            <Button
              variant="primary"
              onClick={generateOptions}
              disabled={loading || !formData.customer_entitlement || !formData.how_you_help || !formData.superpower}
            >
              {loading ? 'Generating...' : 'Generate Vision Statements'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderGenerated = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Choose Your Vision Statement</h2>

        <p className="text-muted mb-4">
          Select the vision statement that best represents your future vision, or choose one to customize.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        {options.map((option, index) => (
          <Card
            key={index}
            className={`mb-3 ${selectedOption === index ? 'border-primary' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => selectOption(index)}
          >
            <Card.Body>
              <Form.Check
                type="radio"
                name="vision"
                id={`option-${index}`}
                checked={selectedOption === index}
                onChange={() => selectOption(index)}
                label={
                  <div>
                    <h5 className="mb-2">Option {index + 1}</h5>
                    <p className="mb-0">{option}</p>
                  </div>
                }
              />
            </Card.Body>
          </Card>
        ))}

        {selectedOption !== null && (
          <Card className="mb-3 bg-light">
            <Card.Body>
              <Form.Label><strong>Customize (Optional):</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editedVision}
                onChange={handleEditChange}
              />
            </Card.Body>
          </Card>
        )}

        <div className="text-center mb-3">
          <Button
            variant="link"
            onClick={regenerate}
            disabled={loading}
          >
            🔄 Regenerate Options
          </Button>
        </div>

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/dashboard/foundation')}
          >
            Save for Later
          </Button>
          <Button
            variant="primary"
            onClick={saveVision}
            disabled={loading || selectedOption === null}
          >
            {loading ? 'Saving...' : 'Save Vision Statement'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderComplete = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Vision Statement Complete</h2>

        <Alert variant="success">
          Your vision statement has been saved!
        </Alert>

        <Card className="mb-4 border-success">
          <Card.Body>
            <h5 className="mb-3">Your Vision:</h5>
            <p className="lead">{savedVision}</p>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard/foundation')}>
            Back to Dashboard
          </Button>
          <Button variant="primary" onClick={() => setStage('input')}>
            Edit Vision Statement
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container className="py-5">
      <BackToDashboard module="foundation" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          {stage === 'input' && renderInput()}
          {stage === 'generated' && renderGenerated()}
          {stage === 'complete' && renderComplete()}
        </Col>
      </Row>
    </Container>
  );
}

export default Vision;
